import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Clock, Users, Loader2, X, MapPin, Check, ExternalLink, Sparkles } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { PageIntro } from "@/components/layout/PageIntro";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { GhostButton, StoreButton } from "@/components/Buttons";
import { SellerListings } from "@/components/SellerListings";
import { SoldBy, StoreContextBar } from "@/components/SoldBy";
import { useMall } from "@/context/MallContext";
import { cardTheme, sortStoreFirst } from "@/lib/mall";
import { MallTag } from "@/components/mall/StoreTag";
import { PageBanner } from "@/components/mall/PageBanner";

const inputCls = "w-full rounded-xl border border-white/15 bg-brand-ink px-4 py-3 text-white placeholder:text-zinc-500 focus:border-zinc-900 focus:outline-none";

function RegisterModal({ cls, onClose }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "" });
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const submit = async (e) => {
        e?.preventDefault();
        if (!form.name || !form.email) { toast.error("Name and email required"); return; }
        setBusy(true);
        try {
            await api.post(`/classes/${cls.id}/register`, form);
            toast.success("Request sent — we'll text or email you within a business day to lock in a time.");
            onClose();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="class-modal">
            <div className="absolute inset-0 bg-black/70" onClick={onClose} />
            <form onSubmit={submit} className="relative w-full max-w-md rounded-3xl border border-white/10 bg-brand-ink p-6">
                <button type="button" onClick={onClose} className="absolute right-4 top-4 text-zinc-400 hover:text-white" data-testid="class-modal-close"><X /></button>
                <h3 className="font-display text-2xl font-bold">{cls.title}</h3>
                <p className="mt-1 text-sm text-zinc-400">
                    ${cls.price} / {cls.price_unit || "person"} · {cls.duration} · up to {cls.seats} people
                </p>
                <div className="mt-5 space-y-3">
                    <input className={inputCls} placeholder="Name" value={form.name} onChange={set("name")} data-testid="class-name" />
                    <input className={inputCls} placeholder="Email" value={form.email} onChange={set("email")} data-testid="class-email" />
                    <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="class-phone" />
                    <StoreButton theme={cardTheme(cls, cls?.store)} type="submit" className="w-full" disabled={busy} data-testid="class-submit">
                        {busy ? <Loader2 className="animate-spin" size={16} /> : "Send request"}
                    </StoreButton>
                    <p className="text-center text-[11px] text-zinc-500">You do not pay now. The shop confirms your date first.</p>
                </div>
            </form>
        </div>
    );
}

export default function Classes() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const { slug: activeSlug, store: activeStore, ensureStore } = useMall();
    useEffect(() => { ensureStore(); }, [ensureStore]);
    useEffect(() => { api.get("/classes").then(({ data }) => setRows(data.classes || [])).catch(() => { }); }, []);

    const sorted = useMemo(() => sortStoreFirst(rows, activeSlug), [rows, activeSlug]);
    const cls = sorted[0] || null;
    const others = sorted.slice(1);
    const t = cardTheme(cls, activeStore);

    return (
        <Layout>
            <PageBanner room="classes" />
            <PageIntro trail={[{ label: "Classes" }]} kicker="Classes · every shop teaches its own" title="Printing classes." testid="classes-intro"
                description="Hands-on classes taught inside real print shops. Each shop posts its own class, price and date here; you pick a seat and pay the shop. The next class up is below, followed by every other class on 13thav." />
            {!cls && (
                <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6" data-testid="classes-empty">
                    <p className="max-w-xl text-base text-zinc-600">
                        No shop has posted a class yet. Shops post their printing classes from the back office, and students book them here.
                    </p>
                </section>
            )}
            {cls && (
                <>
                    <section className="dark-block relative overflow-hidden border-b border-white/10">
                        <img src={mediaUrl(cls.image)} alt="Brooklyn print studio" className="absolute inset-0 h-full w-full object-cover opacity-30" />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/80 to-brand-ink/40" />
                        <div className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6">
                            <Reveal>
                                <MallTag label="Every shop · each teaches its own" />
                                <p className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-brand-blue">
                                    <MapPin size={13} /> {cls.location}
                                </p>
                                <h2 className="mt-4 font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl" data-testid="classes-title">
                                    LEARN IN OUR SHOP.
                                </h2>
                                <p className="mt-4 max-w-2xl text-base text-zinc-300 md:text-lg">{cls.tagline || cls.description}</p>
                                <div className="mt-7 flex flex-wrap gap-2">
                                    {(cls.stats || []).map((s) => (
                                        <span key={s} className="rounded-full border border-white/15 bg-black/40 px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-widest text-zinc-300">{s}</span>
                                    ))}
                                </div>
                            </Reveal>
                        </div>
                    </section>

                    <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                        <Reveal>
                            <div className="grid gap-8 overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] lg:grid-cols-2" data-testid={`class-card-${cls.id}`}>
                                <img src={mediaUrl(cls.image)} alt={cls.title} className="h-full min-h-[280px] w-full object-cover" />
                                <div className="flex flex-col p-7 lg:py-10 lg:pr-10">
                                    <h2 className="font-display text-3xl font-black leading-tight tracking-tight">{cls.title}</h2>
                                    <StoreContextBar store={cls.store} className="mt-3" />
                                    <p className="mt-3 text-base text-zinc-300">{cls.description}</p>
                                    <div className="mt-5 flex flex-wrap gap-4 font-mono text-xs uppercase tracking-widest text-zinc-300">
                                        <span className="inline-flex items-center gap-1.5"><Clock size={14} style={{ color: t.primary }} /> {cls.duration}</span>
                                        <span className="inline-flex items-center gap-1.5"><Users size={14} style={{ color: t.primary }} /> up to {cls.seats} people</span>
                                    </div>
                                    <p className="mt-5 font-mono text-4xl font-black" style={{ color: t.primary }} data-testid="class-price">
                                        ${cls.price}<span className="text-base text-zinc-400"> / {cls.price_unit || "person"}</span>
                                    </p>
                                    <ul className="mt-6 space-y-2">
                                        {(cls.highlights || []).map((h) => (
                                            <li key={h} className="flex gap-2.5 text-sm text-zinc-300">
                                                <Check size={16} className="mt-0.5 shrink-0" style={{ color: t.primary }} /> {h}
                                            </li>
                                        ))}
                                    </ul>
                                    <div className="mt-8 flex flex-wrap gap-3">
                                        <StoreButton theme={t} onClick={() => setOpen(true)} data-testid={`class-register-${cls.id}`}>Book with us</StoreButton>
                                        {(cls.booking_links || []).map((b) => (
                                            <a key={b.url} href={b.url} target="_blank" rel="noreferrer" data-testid={`class-book-${b.label.split(" ").pop().toLowerCase()}`}>
                                                <GhostButton>{b.label} <ExternalLink size={13} /></GhostButton>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </Reveal>

                        {(cls.gallery || []).length > 0 && (
                            <>
                                <h2 className="mt-16 font-display text-3xl font-black tracking-tight">Inside the studio</h2>
                                <div className="mt-6 grid gap-4 sm:grid-cols-3" data-testid="class-gallery">
                                    {cls.gallery.map((g, i) => (
                                        <Reveal key={g} delay={i * 0.06}>
                                            <img src={mediaUrl(g)} alt="Studio" className="aspect-[4/5] w-full rounded-3xl border border-white/10 object-cover" />
                                        </Reveal>
                                    ))}
                                </div>
                            </>
                        )}
                    </section>
                </>
            )}

            {others.length > 0 && (
                <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6" data-testid="mall-classes">
                    <h2 className="font-display text-3xl font-black tracking-tight">More classes from other shops</h2>
                    <p className="mt-2 text-sm text-zinc-400">Every shop on 13thav that teaches is listed here. Other shops show in black and white.</p>
                    <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {others.map((c) => {
                            const ct = cardTheme(c, activeStore);
                            return (
                                <div key={c.id} className={`flex flex-col rounded-3xl border border-white/10 bg-white/[0.03] p-6 ${activeSlug && c.store?.slug === activeSlug ? "" : "grayscale"}`} data-testid={`mall-class-${c.id}`}>
                                    <p className="font-display text-lg font-black leading-tight">{c.title}</p>
                                    <SoldBy store={c.store} className="mt-2" />
                                    <p className="mt-2 text-sm text-zinc-400">{c.duration} · up to {c.seats} people</p>
                                    <p className="mt-3 font-mono text-2xl font-black" style={{ color: ct.ink || ct.primary }}>
                                        ${c.price}<span className="text-xs text-zinc-500"> / {c.price_unit || "person"}</span>
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <SellerListings
                kind="class"
                heading="Classes from other teachers"
                blurb="Printers, crafters and designers across the city post their own workshops here. Message the teacher directly to book — free to post while we grow."
                sellCta="Teach a class"
                testid="community-classes"
            />

            <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6">
                <div className="flex flex-col items-start gap-4 rounded-3xl border border-white/15 bg-white/[0.03] p-8 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500"><Sparkles size={13} /> Teach with us</p>
                        <h3 className="mt-2 font-display text-2xl font-black tracking-tight">Know how to print? Fill your seats here.</h3>
                        <p className="mt-1 text-sm text-zinc-300">Post your workshop, we review it within a day, students message you straight from the listing.</p>
                    </div>
                    <a href="/supplies/sell?kind=class"><StoreButton theme={t} data-testid="teach-cta">Post your class</StoreButton></a>
                </div>
            </section>

            {open && cls && <RegisterModal cls={cls} onClose={() => setOpen(false)} />}
        </Layout>
    );
}
